import { convertToDoubleDigits } from 'utils/NumberUtils';
import moment from 'moment';
import { getUserCulture } from './UserCulture';
import { LiveColumnDisplayFormat } from '@decisions/server/DecisionsFramework/Design/Report/InlineFields/LiveColumnDisplayFormat';

// TODO .NET ticks are not something we should be leaking into the UI in the first place.
// There are proper ways to serialize them for JS consumption, which generally involves UTC
// or milliseconds since epoch, not ticks (100 nanoseconds)
const BASE_TICKS = 621355968000000000;

export const MinDate = '0001-01-01';
export const MaxDate = '9999-12-31';

export interface TimeDuration {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export function getMoment() {
  // use window.moment, to work around something funny with this setup and webpack externals,
  // which I don't have time to sort out, right now.
  return (window as any).moment as typeof moment;
}

export function getDateMillisFromTicks(ticks?: number) {
  return ticks ? (ticks - BASE_TICKS) / 10000 : 0;
}

export function getMomentFromString(dateString: string, justDate = false) {
  const moment = getMoment().parseZone(dateString);

  if (justDate && moment.isValid()) {
    return getMoment()(moment.format('LL'));
  }

  return moment;
}

export function getMomentFromISOString(dateString: string, justDate = false) {
  const moment = getMoment()(dateString);

  if (justDate && moment.isValid()) {
    return getMoment()(moment.format('LL'));
  }

  return moment;
}

export function getMomentFromMillis(millis: number, justDate = false) {
  if (!justDate) {
    return getMoment()(millis);
  }
  const dateWithoutTimeZone = getDateIrrespectiveOfTimezone(millis); // for Calendar editor, really.
  return getMoment()(dateWithoutTimeZone);
}

export function getMomentFromTicks(ticks: number) {
  const millis = getDateMillisFromTicks(ticks);
  return getMomentFromMillis(millis);
}

export function getDateIrrespectiveOfTimezone(millis: number) {
  // if millis is falsy, it's very unlikely we want the epoch
  const date = millis ? new Date(millis) : new Date();
  // (below comments from Akash in Editors.ts#CalendarEditor#Update)
  // get the date regardless of timezone.
  // To-do: need to find a better way to get the date regardless of timezone in javascript.
  return date.toUTCString().slice(0, 16); //the toUTCString will always return the date in a specific format i.e. Thu, 04 May 2017 00:00:00 GMT
  // so to get rid from timeZone we need to slice it in a specific range and according to my research this is the only way to generate date regardless of timezone
  // if you know the better way then please update it.
  // there are other methods that will return the date without timezone i.e. .toDateString() but this will first parse the date in local timezone then return it that may
  // differ from the originally selected date.
  // for more info please check the bug-[DT-010685] RVSP - Calendar filter - Selecting any date on calendar filter, its previous date gets selected.
}

export function convertToMomentDateFormat(dateFormat: string): string {
  var momentDateFormat = '';
  if (dateFormat) {
    //Date format needs to be capital to convert date in different cultures
    var chars = { d: 'D', y: 'Y' };
    momentDateFormat = dateFormat
      .replace(/[dy]/g, m => chars[m as keyof typeof chars])
      .replace('tt', 'A')
      .replace('DDDD', 'dddd');
  }
  return momentDateFormat;
}

export function getFormattedDateString(
  date?: moment.Moment | string | Date | null,
  format?: string,
  useStrict = false
): string {
  format = format ? format : getUserCulture().DateTimeFormat;
  //date or format can not be null or empty.
  if (!date || !format) {
    return '';
  }
  let momentDate = getMomentDateObject(date, format, useStrict);
  // can simplify a lot by normalizing on moment?
  if (!momentDate.isValid()) {
    return '';
  }
  return momentDate.format(convertToMomentDateFormat(format));
}

export function getFormattedDateStringForServer(date: moment.Moment, onlyDate = false) {
  if (onlyDate) {
    // set time to zero
    date
      .hours(0)
      .minutes(0)
      .seconds(0);
  }
  return getFormattedDateString(date, 'M/d/yyyy H:mm:ss');
}

export function getMomentDateObject(date: any, dateFormat?: string, useStrict: boolean = false): moment.Moment {
  if (!date || !dateFormat) return getMoment()(date);
  dateFormat = convertToMomentDateFormat(dateFormat);
  if (getMoment().isMoment(date)) return date;

  if (date instanceof Date) {
    return getMoment()(moment(date).format(dateFormat), dateFormat, useStrict);
  }
  let momentObj = getMoment()(date, dateFormat, useStrict);
  if (momentObj.isValid() || useStrict)
    /*able to cast date in the given format or using Strict mode*/
    return momentObj;
  else
    return getMoment()(
      date,
      undefined,
      useStrict
    ); /*if the date is string but in UTC format the we do not need to specify the format.*/
}

export function isValidDateTime(
  date: string,
  dateFormat: string,
  allowNull: boolean = false,
  useStrict: boolean = false
): boolean {
  // need to add invalid date check because of bug in isValid function i.e. if date is 7/17/2017 4:21:00 A and
  // if format contains 'A' it does not check for AM and considers it as valid date
  return (
    isValidDate(date, dateFormat, allowNull, useStrict) && getFormattedDateString(date, dateFormat) !== 'Invalid date'
  );
}

export function isValidDate(
  date: string | null | undefined,
  dateFormat: string,
  allowNull: boolean = false,
  useStrict: boolean = false
): boolean {
  if (allowNull === true && !date) {
    return true;
  }
  // TODO going back and forth between moment and string 2x to check this seems expensive:
  return getMoment()(getFormattedDateString(date, dateFormat, useStrict), dateFormat, true).isValid();
}

export function isISODateString(dateString: string) {
  if (!dateString || !dateString.trim()) return false;
  return getMoment()(dateString, getMoment().ISO_8601, true).isValid();
}

/**Checks if a .net min date */
export function isMinDate(date: string | moment.Moment) {
  if (!date) return false;
  const csharpMinDate = ['01/01/0001', '1/1/0001', '0001-01-01T00:00:00Z'];
  let isISODate = false;
  if (typeof date === 'string') {
    const dateString = date.split(' ')[0];
    if (csharpMinDate.indexOf(dateString) > -1) return true;
    isISODate = isISODateString(date);
  }
  // need to consider utc time here
  const dateToFormat = isISODate ? new Date(date.toString()) : date;
  const formattedDateString = getMoment()(dateToFormat, 'D/M/YYYY h:mm:ss').format(
    convertToMomentDateFormat('dd/MM/yyyy')
  );
  return csharpMinDate.indexOf(formattedDateString) > -1;
}

export function isDotNetJSONDate(dateText: string): boolean {
  if (!dateText) return false;
  return typeof dateText === 'string' && dateText.startsWith('/Date(');
}

export function getISODateStringWithUtcOffset(date: Date) {
  if (!date) return date;
  //When you create a date with new Date(str) it creates a date object with a time zone. toISOString() makes it zero UTC offset, as denoted by the suffix "Z".
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
}

export function getTimeFormatBetweenTwoDates(date1: Date, date2: Date) {
  let timeDuration = getTimeDurationBetweenTwoDates(date1, date2);
  return getTimeSpanString(timeDuration);
}

export function getFormatedTimeBetweenTwoDates(date1: Date, date2: Date, displayFormat: LiveColumnDisplayFormat) {
  let duration = getDurationBetweenDates(date1, date2);
  if (displayFormat === LiveColumnDisplayFormat.Days) return getDayString(Math.floor(duration.asDays()));
  else if (displayFormat === LiveColumnDisplayFormat.Hours) return getHourString(Math.floor(duration.asHours()));
  else if (displayFormat === LiveColumnDisplayFormat.Minutes) return getMinuteString(Math.floor(duration.asMinutes()));
  return getTimeFormatBetweenTwoDates(date1, date2); //auto format by default
}

export function getTimeDurationBetweenTwoDates(date1: Date, date2: Date) {
  let duration = getDurationBetweenDates(date1, date2);
  let days = Math.floor(duration.asDays());
  let hours = Math.floor(duration.asHours());
  let minutes = Math.floor(duration.minutes());
  let seconds = Math.floor(duration.seconds());
  return { days: days, hours: hours, minutes: minutes, seconds: seconds } as TimeDuration;
}

export function getDurationBetweenDates(date1: Date, date2: Date) {
  let milliseconds = getDateDifferenceInMilliseconds(date1, date2);
  return getMoment().duration(milliseconds);
}

export function getDateDifferenceInMilliseconds(date1: Date, date2: Date) {
  let milliseconds = Math.abs(date1.getTime() - date2.getTime());
  return milliseconds;
}

function getTimeSpanString(timeDuration: TimeDuration) {
  let timeFormat = '';
  if (timeDuration.days > 0) {
    timeFormat = getDayString(timeDuration.days);
    if (timeDuration.days < 5) timeFormat = `${timeFormat} ${getHourString(timeDuration.hours)}`;
  } else if (timeDuration.hours > 0) {
    timeFormat = getHourString(timeDuration.hours);
    if (timeDuration.hours < 5) timeFormat = `${timeFormat} ${getMinuteString(timeDuration.minutes)}`;
  } else if (timeDuration.minutes > 0) {
    timeFormat = getMinuteString(timeDuration.minutes);
    if (timeDuration.minutes < 5) timeFormat = `${timeFormat} ${getSecondString(timeDuration.seconds)}`;
  } else {
    timeFormat = getSecondString(timeDuration.seconds);
  }
  return timeFormat;
}

function getDayString(days: number) {
  let daysInString = convertToDoubleDigits(days);
  if (days === 1) return `${daysInString} day`;
  return `${daysInString} days`;
}

function getHourString(hours: number) {
  let hoursInString = convertToDoubleDigits(hours);
  if (hours === 1) return `${hoursInString} hr`;
  return `${hoursInString} hrs`;
}

function getMinuteString(minutes: number) {
  let minutesInString = convertToDoubleDigits(minutes);
  if (minutes === 1) return `${minutesInString} min`;
  return `${minutesInString} mins`;
}

function getSecondString(seconds: number) {
  let secondsInString = convertToDoubleDigits(seconds);
  if (seconds === 1) return `${secondsInString} sec`;
  return `${secondsInString} secs`;
}

export function isSameDate(date1: string | moment.Moment, date2: string | moment.Moment, dateFormat?: string): boolean {
  //in case of empty or null dates.
  //Note: Please do not use .isSame() from moment because it does not consider time while comparing date-time values.
  //it will work fine if you have to compare date only.
  if ((!date1 && !date2) || (isMinDate(date1) && !date2) || (isMinDate(date2) && !date1)) return true;
  //as per the first if block it is clear that both the dates are not empty.
  //so checking if any one of them is empty and other has some value, then both are not the same dates.
  if (!date1 || !date2) return false;
  return getFormattedDateString(date1, dateFormat) === getFormattedDateString(date2, dateFormat);
}

export function getCurrentCultureMomentTimeFormat() {
  const userCulture = getUserCulture();
  return userCulture.TimeFormat.replace('tt', 'A');
}

/**
 * Evaluate whether a string is intended to be evaluated as a date _quickly_.
 *
 * This is intended to be _fast_ not perfect. If you need something foolproof use moment, but for high-iteration
 * contexts, moment is a bit slow.
 *
 * This will match strings like:
 *
 * * 0001-01-01T00:00:00Z
 * * 2020-04-27 05:45:00Z
 * * 2020-04-27 05:45
 * * 0001-01-01T
 * * 2020-04-27
 *
 * @param value to evaluate
 */
export function isIsoDateStringQuickAndDirty(value: string) {
  return !!value.match(/(\d\d\d\d)-(\d\d)-(\d\d)[T ]?(?:(\d\d):(\d\d))?(?::(\d\d))?Z?/);
}
